import React, { useEffect, useState } from "react";
import {
  VariantControl,
  type VariantType,
} from "../../components/variant/VariantControl";
import type { NewPropertyState } from "../../../usePropertyStateV2";
import { useTailwindProperties } from "@/hooks/useTailwindProperties";
import {
  type ClassTypeWithPropertyType,
  PropertyType,
} from "@/tailwind/tailwindUtils";
import {
  calculateNewClasses,
  getIndexFromStyles,
  getStylesFromIndex,
} from "../../utils";
import {
  AutoLayoutSelector,
  type ComboBoxOptionType,
} from "../../components/input";
import { UpdateClassesChange } from "@/store/zustand/changeLedgerTypes";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useSelectedNavTreeNode } from "@/components/Canvas/stores/canvasStore";
import { TempoElement } from "@/utils/tempoElement";

// TODO: Split all fields out from the TailwindStylingPanelV2.tsx file like this

interface AlignProps {
  justifyContentState: NewPropertyState;
  alignItemsState: NewPropertyState;
  flexDirectionState: NewPropertyState;
  elementClasses: string[];
  tailwindClassesMap: Record<string, ClassTypeWithPropertyType>;
  pickTailwindClassForProperty: (props: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  clearTailwindClassForProperty: (props: {
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
}

const AlignField: React.FC<AlignProps> = React.memo(
  ({
    alignItemsState,
    flexDirectionState,
    justifyContentState,
    elementClasses,
    tailwindClassesMap,
    pickTailwindClassForProperty,
    clearTailwindClassForProperty,
  }) => {
    const justifyContentProperties = useTailwindProperties(
      PropertyType.JUSTIFY_CONTENT,
    );
    // TODO: Add flex direction properties
    // const flexDirectionProperties = useTailwindProperties(
    //   PropertyType.FLEX_DIRECTION
    // );
    const alignItemsProperties = useTailwindProperties(
      PropertyType.ALIGN_ITEMS,
    );
    const { selectedElement } = useSelectedNavTreeNode();

    const [layoutIndex, setLayoutIndex] = useState(0);
    const [alignVariantUpdate, setAlignVariantUpdate] = useState(0);

    useEffect(() => {
      const index = getIndexFromStyles(
        flexDirectionState.propertyValue,
        justifyContentState.propertyValue,
        alignItemsState.propertyValue,
      );
      setLayoutIndex(index);
    }, [
      flexDirectionState.propertyValue,
      justifyContentState.propertyValue,
      alignItemsState.propertyValue,
      selectedElement,
    ]);

    const addLayout = (index: number, variant?: string) => {
      const styles = getStylesFromIndex(
        flexDirectionState.propertyValue,
        index,
      );
      const tailwindItemMatchJustifyContent = justifyContentProperties.find(
        (property) =>
          property.css.startsWith(`justify-content: ${styles.justifyContent};`),
      );
      const tailwindItemMatchAlignItems = alignItemsProperties.find(
        (property) =>
          property.css.startsWith(`align-items: ${styles.alignItems};`),
      );

      let resultClasses = elementClasses;

      const involvedClasses: ComboBoxOptionType[] = [];
      if (tailwindItemMatchJustifyContent) {
        resultClasses = calculateNewClasses({
          option: tailwindItemMatchJustifyContent,
          propertyType: PropertyType.JUSTIFY_CONTENT,
          oldClasses: resultClasses,
          tailwindClassesMap,
          variant,
        });
        involvedClasses.push(tailwindItemMatchJustifyContent);
      }
      if (tailwindItemMatchAlignItems) {
        resultClasses = calculateNewClasses({
          option: tailwindItemMatchAlignItems,
          propertyType: PropertyType.ALIGN_ITEMS,
          oldClasses: resultClasses,
          tailwindClassesMap,
          variant,
        });
        involvedClasses.push(tailwindItemMatchAlignItems);
      }

      const updateClassesChange = new UpdateClassesChange({
        codebaseIdToUpdateClass: selectedElement?.codebaseId || "",
        oldClasses: elementClasses,
        newClasses: resultClasses,
        involvedClasses: involvedClasses,
        isInstantChangeOnly: false,
      });

      useDesignCommandManager.getState().executeCommand(updateClassesChange);
    };

    return (
      <div className="flex flex-col items-start gap-1">
        <div className="text-xs w-full text-text-secondary font-normal flex items-center relative">
          Align
          {/* VariantControl icon and dot */}
          <div className="absolute right-0 top-0" />
        </div>
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div className="w-full">
              <AutoLayoutSelector
                selected={layoutIndex}
                setSelected={(selected) => {
                  addLayout(selected);
                  setLayoutIndex(selected);
                }}
                row={flexDirectionState.propertyValue?.startsWith("row")}
              />
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent className="p-0 border-none">
            <VariantControl
              key={alignVariantUpdate}
              valueByVariant={alignItemsState.valueByVariant}
              onVariantChange={(variant: VariantType, value: number) => {
                const styles = getStylesFromIndex(
                  flexDirectionState.propertyValue,
                  value,
                );
                const tailwindItemMatchJustifyContent =
                  justifyContentProperties.find((property) =>
                    property.css.startsWith(
                      `justify-content: ${styles.justifyContent};`,
                    ),
                  );
                const tailwindItemMatchAlignItems = alignItemsProperties.find(
                  (property) =>
                    property.css.startsWith(
                      `align-items: ${styles.alignItems};`,
                    ),
                );
                if (tailwindItemMatchJustifyContent) {
                  pickTailwindClassForProperty({
                    option: tailwindItemMatchJustifyContent,
                    propertyType: PropertyType.JUSTIFY_CONTENT,
                    setTemporaryDraggingValue:
                      justifyContentState.setTemporaryDraggingValue,
                    variant,
                  });
                }
                if (tailwindItemMatchAlignItems) {
                  pickTailwindClassForProperty({
                    option: tailwindItemMatchAlignItems,
                    propertyType: PropertyType.ALIGN_ITEMS,
                    setTemporaryDraggingValue:
                      alignItemsState.setTemporaryDraggingValue,
                    variant,
                  });
                }
                addLayout(value, variant);
                setAlignVariantUpdate((v) => v + 1);
              }}
              onClearVariant={(variant: VariantType) => {
                clearTailwindClassForProperty({
                  propertyType: PropertyType.ALIGN_ITEMS,
                  setTemporaryDraggingValue:
                    alignItemsState.setTemporaryDraggingValue,
                  variant,
                });
                clearTailwindClassForProperty({
                  propertyType: PropertyType.JUSTIFY_CONTENT,
                  setTemporaryDraggingValue:
                    justifyContentState.setTemporaryDraggingValue,
                  variant,
                });
              }}
              currentFlexDirection={flexDirectionState.propertyValue}
              currentJustifyContent={justifyContentState.propertyValue}
              currentAlignItems={alignItemsState.propertyValue}
              controlType="align"
            >
              {(variant, expanded, setExpanded) => {
                // Use the align-items and justify-content values for the current variant
                const flexDirection = flexDirectionState.propertyValue;
                let justifyContent = justifyContentState.propertyValue;
                let alignItems = alignItemsState.propertyValue;
                if (variant) {
                  if (justifyContentState.valueByVariant[variant]?.value) {
                    justifyContent =
                      justifyContentState.valueByVariant[variant].value;
                  }
                  if (alignItemsState.valueByVariant[variant]?.value) {
                    alignItems = alignItemsState.valueByVariant[variant].value;
                  }
                }
                const selectedIndex = getIndexFromStyles(
                  flexDirection,
                  justifyContent,
                  alignItems,
                );
                return expanded && variant ? (
                  <AutoLayoutSelector
                    selected={selectedIndex}
                    setSelected={(selected) => {
                      // Update only this variant's value
                      const styles = getStylesFromIndex(
                        flexDirectionState.propertyValue,
                        selected,
                      );
                      const tailwindItemMatchJustifyContent =
                        justifyContentProperties.find((property) =>
                          property.css.startsWith(
                            `justify-content: ${styles.justifyContent};`,
                          ),
                        );
                      const tailwindItemMatchAlignItems =
                        alignItemsProperties.find((property) =>
                          property.css.startsWith(
                            `align-items: ${styles.alignItems};`,
                          ),
                        );
                      if (tailwindItemMatchJustifyContent) {
                        pickTailwindClassForProperty({
                          option: tailwindItemMatchJustifyContent,
                          propertyType: PropertyType.JUSTIFY_CONTENT,
                          setTemporaryDraggingValue:
                            justifyContentState.setTemporaryDraggingValue,
                          variant,
                        });
                      }
                      if (tailwindItemMatchAlignItems) {
                        pickTailwindClassForProperty({
                          option: tailwindItemMatchAlignItems,
                          propertyType: PropertyType.ALIGN_ITEMS,
                          setTemporaryDraggingValue:
                            alignItemsState.setTemporaryDraggingValue,
                          variant,
                        });
                      }
                      addLayout(selected, variant);
                      setAlignVariantUpdate((v) => v + 1);
                      setExpanded(false);
                    }}
                    row={flexDirection?.startsWith("row")}
                  />
                ) : null;
              }}
            </VariantControl>
          </ContextMenuContent>
        </ContextMenu>
      </div>
    );
  },
);

export default AlignField;
